import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/system";
import { GridRowModes } from "@mui/x-data-grid";
import {StyledConfigurationContainer,StyledIndexContainerDiv, StyledLeadContainer,} from "../../../Components/Styled";
import DataGridTable from "../../../Components/DataGrid";
import {ADDITIONAL_ATTRIBUTE_FIELDS, ATTRIBUTE_TYPE_TEXT, customizationDropdown,handleValidateCodevalue,PICK_LIST_TYPE_ARRAY,STATIC_ENTITY_CONFIG,} from "../../../Utility/constant";
import BiztantuLoader from "../../../Components/Loader";
import SelectBoxWithLabel from "../../../Components/SelectBoxWithLable";
import {requestGetAllTextfieldDetails, requestGetTextfieldDetails} from "../../../Store/Customization/action";
import { requestShowLoader, requestHideLoader } from '../../../Store/Global/action';
import { createRequestData, updateRequestData } from "../../../Utility/apiHelper";
import { useSnackbar } from "notistack";
import TextFiledWithLabel from "../../../Components/TextFieldWithLabel";
import AllowedStatusDialog from "./AllowedStatusDialog";
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import ClientThemeDialog from "./ClientThemeDialog";

const CustomizationPage = () => {
  const dispatch = useDispatch();

  const [selectedEntity, setSelectedEntity] = useState("LEAD");
  const [dataGridData, setDatagridData] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [rowModesModel, setRowModesModel] = useState({});
  // Accumulates every committed edit as DataGrid calls processRowUpdate (synchronous, no state timing dependency)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const internationalise = useSelector((state) => state?.internationalise?.currentHashMapData);
  const isLoadingProgress = useSelector((state) => state?.global?.isLoading);
  const entityDetails = useSelector((state) => state?.customization?.entityDetails?.columnJson);
  const entityId = useSelector((state) => state?.customization?.entityDetails?.id);

  const currentUser = useSelector(state => state?.owner?.currentuser);
  const currentClientId = currentUser?.client?.id
  const isCellEditable = (params) => {
  const columnField = params.field;
  const rowField = params?.row?.field;
  const editableFields = [...ADDITIONAL_ATTRIBUTE_FIELDS,"title","ageGroup","maritalStatus","type","income"]
  // visibility + mandatory
  if (["visibility", "mandatory"].includes(columnField)) {
    return params.row.editable ?? true;
  }

  // type column itself should be editable for additional attributes
  if (columnField === "type") {
    return editableFields.includes(rowField);
  }

  // options column: only editable if type is Pick List
  if (columnField === "options") {
    return (
      editableFields.includes(rowField) &&
      PICK_LIST_TYPE_ARRAY.includes(
        params.row.type
      )
    );
  }

  return false;
};
  useEffect(() => {
    dispatch(requestGetTextfieldDetails(selectedEntity));
  }, [dispatch, selectedEntity]);

  const hasExistingConfig = useMemo(() => {
    return !!entityDetails && Object.keys(entityDetails || {}).length > 0 && entityId;
  }, [entityDetails]);

  const buildRows = useCallback(() => {
    const entityFields = STATIC_ENTITY_CONFIG?.[selectedEntity] || {};
    const dbFieldMap = entityDetails || {};

    return Object.entries(entityFields)?.map(([fieldKey, fieldConfig], index) => {
      const dbField = dbFieldMap?.[fieldConfig?.name] || {};
      const fieldType = dbField?.type || fieldConfig?.type;
      const isPicklist = PICK_LIST_TYPE_ARRAY.includes(fieldType);
      

      const availableOptions =
        dbField && Array?.isArray(dbField?.options)
          ? dbField.options
          : fieldConfig?.options || [];
      return {
        id: index + 1,
        field: fieldKey,
        name: fieldConfig?.name || "",
        label: handleValidateCodevalue(
          internationalise,
          fieldConfig?.label || ""
        ),
        labelKey: fieldConfig?.label || "",
        visibility: dbField?.visibility ?? fieldConfig?.visibility,
        mandatory: dbField?.mandatory ??  fieldConfig?.mandatory,
        editable: fieldConfig?.editable ?? true,
        type: dbField?.type  || fieldConfig?.type || "N/A",
        options: !fieldType
        ? "N/A"
        : isPicklist
          ? availableOptions.length
            ? availableOptions
                ?.map((item) => item.label || item.name || item)
                .join(", ")
            : ""
          : ADDITIONAL_ATTRIBUTE_FIELDS.includes(fieldConfig?.name) ? "" : "N/A",
      };
    });
  }, [selectedEntity, internationalise, entityDetails]);

  useEffect(() => {
    const rows = buildRows();
    setDatagridData(rows);
    setOriginalRows(rows);
    setIsEditMode(false);
    setRowModesModel({});
  }, [buildRows]);

const processRowUpdate = useCallback((newRow, oldRow) => {
  const isChanged = JSON.stringify(newRow) !== JSON.stringify(oldRow);

  if (isChanged) {
    setHasUnsavedChanges(true);
  }

  setDatagridData((prev) =>
    prev?.map((row) => (row.id === newRow.id ? newRow : row))
  );

  return newRow;
}, []);

const handleEditAll = useCallback(() => {
  setIsEditMode(true);

  const newModel = {};
  dataGridData.forEach((item) => {
    if (item.editable !== false) {
      newModel[item.id] = { mode: GridRowModes.Edit };
    }
  });

  setRowModesModel(newModel);
}, [dataGridData]);

const handleDone = useCallback(() => {
  const viewModeModel = {};
  dataGridData.forEach((item) => {
    viewModeModel[item.id] = { mode: GridRowModes.View };
  });

  setRowModesModel(viewModeModel);
  setIsEditMode(false);
}, [dataGridData]);

const handleCancelAll = useCallback(() => {
  setDatagridData(originalRows);
  setHasUnsavedChanges(false);
  setIsEditMode(false);

  const newModel = {};
  originalRows.forEach((item) => {
    newModel[item.id] = {
      mode: GridRowModes.View,
      ignoreModifications: true,
    };
  });

  setRowModesModel(newModel);
}, [originalRows]);

  const handleSaveAll = useCallback(async () => {
    if (JSON.stringify(originalRows) === JSON.stringify(dataGridData)) {
      enqueueSnackbar(handleValidateCodevalue(internationalise, "COMMON_NO_DATA_CHANGED"), { variant: "info" });
      return;
    }

    const configPayload = {
      entityName: selectedEntity,
      columnJson: dataGridData.reduce((acc, row) => {
      acc[row.name] = {
        visibility: row.visibility,
        mandatory: row.mandatory,

        // NEW
        type: row.type || "NA",

        // CONVERT STRING TO ARRAY
        options:
          row.type !== ATTRIBUTE_TYPE_TEXT
            ?  row.options
              ?.split(",")
              ?.map((item) => item.trim())
              ?.filter(Boolean)
              ?.map((item) => ({
                name: item
              }))
            : "N/A",
      };
        
        return acc;
      }, {}),
    };

    dispatch(requestShowLoader());
    try {
      let response;
      const url = `${process.env.REACT_APP_BASE_URL}entity-customizations`;

      if (hasExistingConfig) {
        response = await updateRequestData(configPayload, `${url}/${entityId}`);
      } else {
        response = await createRequestData(configPayload, url);
      }

      if (response?.status >= 200 && response?.status <= 300) {
        enqueueSnackbar(
          handleValidateCodevalue(
            internationalise,
            hasExistingConfig ? "COMMON_UPDATED_SUCCESSFULLY" : "COMMON_CREATED_SUCCESSFULLY"
          ),
          { variant: "success" }
        );
        setOriginalRows(dataGridData);
        setHasUnsavedChanges(false);
        setIsEditMode(false);
        await dispatch(requestGetTextfieldDetails(selectedEntity));
        await dispatch(requestGetAllTextfieldDetails());
      }
      else {
        enqueueSnackbar(handleValidateCodevalue(internationalise, (response?.propererror.data?.message || "COMMON_PLEASE_TRY_AGAIN")), { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(handleValidateCodevalue(internationalise, (error?.propererror.data?.message || "COMMON_PLEASE_TRY_AGAIN")), { variant: "error" });
      
    } finally {
      dispatch(requestHideLoader());
    }
  }, [selectedEntity, hasExistingConfig, currentClientId, dispatch, enqueueSnackbar, internationalise, originalRows,dataGridData, entityId]);

  const columns = useMemo(
    () => [
      {
        field: "field",
        headerName: handleValidateCodevalue(internationalise,"CUSTOMIZATION_COL_FIELD"),
        flex: 1.4,
        editable: false,
      },
      {
        field: "label",
        headerName: handleValidateCodevalue(internationalise,"CUSTOMIZATION_COL_LABEL_VALUE"),
        flex: 1.5,
        editable: true,
      },
      {
        field: "visibility",
        headerName: handleValidateCodevalue(internationalise,"CUSTOMIZATION_COL_VISIBILITY"),
        type: "boolean",
        flex: 1,
        editable: true,
      },
      {
        field: "mandatory",
        headerName: handleValidateCodevalue(internationalise,"CUSTOMIZATION_COL_MANDAT"),
        type: "boolean",
        flex: 1,
        editable: true,
      },
      {
        field: "type",
        headerName: handleValidateCodevalue(internationalise,"CUSTOMIZATION_COL_TYPE"),
        type: "singleSelect",
        valueOptions: [ATTRIBUTE_TYPE_TEXT, ...PICK_LIST_TYPE_ARRAY],
        editable: true,
        flex: 1,
      },
      {
      field: "options",
      headerName: handleValidateCodevalue(internationalise, "CUSTOMIZATION_COL_OPTIONS"),
      editable: true,
      flex: 2,
       renderEditCell: (params) => {
        const isPickList = PICK_LIST_TYPE_ARRAY.includes(params.row.type);

        if (!isPickList) {
          return null;
        }

        return (
          <TextFiledWithLabel
          sx = {{width : "100%"}}
            value={params.value || ""}
            placeholder={handleValidateCodevalue(internationalise,"CUSTOMIZATION_ADD_ATT_OPTION_PH")}
            handleOnChange={(event) => {
              params.api.setEditCellValue({
                id: params.id,
                field: "options",
                value: event.target.value,
              });
            }}
          />
        );
      },
    },
    ],
    [internationalise]
  );
  const [openAllowedStatus,setOpenAllowedStatus] = useState(false);
  const [themeDialog,setThemeDialog] = useState(false);
  return (
    <BiztantuLoader isChildren={true} open={isLoadingProgress}>
      <Box sx={{ marginBottom: 0}}>
        <StyledConfigurationContainer>
          <Typography variant="h6">
            {handleValidateCodevalue(
              internationalise,
              "CONF_CUSTOMIZATION_TITLE"
            )}
          </Typography>
        </StyledConfigurationContainer>

      <StyledIndexContainerDiv sx = {{
          backgroundColor:" #f4f9fe",
          marginBottom: "10px",
        }}>
          <StyledLeadContainer>
        <Stack
          padding= "4px 10px"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          className="table-header-search table-header-search-common"
        >
          <SelectBoxWithLabel
            name="selectedEntity"
            value={selectedEntity}
            label={handleValidateCodevalue(internationalise, "CUSTOMIZATION_DD_SELECT_ENTITY")}
            optionMenuItem={customizationDropdown}
            handleOnChange={(event) => setSelectedEntity(event?.target?.value)}
            disabled={isEditMode}
          />
        </Stack>
        <Stack
          padding= "4px 10px"
          direction="row-reverse"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          width = {"70%"}
          >
        <Stack className="create-button-container"
          >
            <Tooltip
                title={handleValidateCodevalue(
                  internationalise,
                  "SET_STATUS_RULES",
                )}
              >
                <Button className="biztantu-sec-btn ml-20" onClick = {()=>setOpenAllowedStatus(true)}>
                  {handleValidateCodevalue(internationalise, "SET_STATUS_RULES")}
                </Button>
              </Tooltip>
              <Tooltip
                title={handleValidateCodevalue(
                  internationalise,
                  "SET_CLIENT_THEME",
                )}
              >
                <Button className="biztantu-sec-btn ml-20" onClick = {()=>setThemeDialog(true)}>
                  {handleValidateCodevalue(internationalise, "SET_CLIENT_THEME")}
                </Button>
              </Tooltip>
              </Stack>
              </Stack>
              </StyledLeadContainer>
              {themeDialog && 
              <ClientThemeDialog
                open = {themeDialog}
                handleClose = {()=>setThemeDialog(false)}
              />}
                {openAllowedStatus && 
                  <AllowedStatusDialog
                    entity = {selectedEntity}
                    open = {openAllowedStatus}
                    handleClose = {()=>setOpenAllowedStatus(false)}
                  />
                }
        <DataGridTable
          isCheckboxReq= {false}
          handleEdit = {handleEditAll}
          handleDone={handleDone}
          handleCancel= {handleCancelAll}
          handleSave= {handleSaveAll}
          hasUnsavedChanges={hasUnsavedChanges}
          isEditMode= {isEditMode}
          isCustomization={true}
          dataGridData={dataGridData}
          isLoading={false}
          height={"calc(100vh - 273px)"}
          marginTop="0px"
          columns={columns}
          unChangedDataWaring ={false}
          isCellEditable = {isCellEditable}
          handleProcessRowUpdate={processRowUpdate}
          rowModesModel={rowModesModel}
        />
      </StyledIndexContainerDiv>
      </Box>
    </BiztantuLoader>
  );
};

export default CustomizationPage;